import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bike, MapPin, ChevronRight, Clock, CheckCircle2, XCircle, Loader2, Utensils, ArrowLeft, RotateCw } from "lucide-react";
import api from "../lib/api";
import { AppHeader } from "../components/AppHeader";

const STATUS_STYLE = {
  awaiting_payment: { color: "text-amber-700", bg: "bg-amber-50", icon: Clock },
  paid: { color: "text-orange-700", bg: "bg-orange-50", icon: Loader2 },
  resto_accepted: { color: "text-orange-700", bg: "bg-orange-50", icon: Loader2 },
  ready_for_pickup: { color: "text-orange-700", bg: "bg-orange-50", icon: Loader2 },
  driver_assigned: { color: "text-blue-700", bg: "bg-blue-50", icon: Bike },
  arrived_at_restaurant: { color: "text-blue-700", bg: "bg-blue-50", icon: Bike },
  picked_up: { color: "text-blue-700", bg: "bg-blue-50", icon: Bike },
  out_for_delivery: { color: "text-blue-700", bg: "bg-blue-50", icon: Bike },
  delivered: { color: "text-emerald-700", bg: "bg-emerald-50", icon: CheckCircle2 },
  completed: { color: "text-emerald-700", bg: "bg-emerald-50", icon: CheckCircle2 },
  canceled: { color: "text-red-700", bg: "bg-red-50", icon: XCircle },
  refused_by_resto: { color: "text-red-700", bg: "bg-red-50", icon: XCircle },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })
      + " à " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

export default function MyOrders() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reordering, setReordering] = useState(null);

  // Recommander : recrée un panier identique en localStorage et redirige vers la vitrine du resto.
  // Réutilise l'infrastructure existante (`nold_active_cart` + `PublicRestaurant.jsx`) — aucun
  // nouveau flux serveur, aucun impact sur le paiement Stripe.
  const reorder = async (o) => {
    if (!o.restaurant_id) {
      toast.error("Ce restaurant n'est plus disponible."); return;
    }
    setReordering(o.id);
    try {
      // Vérifie que le resto est toujours publié — sinon le lien /restaurants/{id} renvoie 404
      const { data: resto } = await api.get(`/restaurants/${o.restaurant_id}`);
      if (!resto?.id) throw new Error("resto introuvable");
      // Reconstruit le panier au format attendu par le flow existant
      const items = (o.items || [])
        .filter((it) => it && (it.name || it.id))
        .map((it) => ({
          id: it.id || it.menu_item_id || it.name,
          name: it.name || "Article",
          price_eur: Number(it.price_eur || 0),
          qty: Number(it.qty || 1),
        }));
      if (!items.length) { toast.error("Cette commande ne contient plus d'articles."); return; }
      const cart = {
        restaurant_id: o.restaurant_id,
        restaurant_name: o.restaurant_name,
        items,
      };
      try { localStorage.setItem("nold_active_cart", JSON.stringify(cart)); } catch { /* ignore */ }
      toast.success(`${items.length} article${items.length > 1 ? "s" : ""} ajouté${items.length > 1 ? "s" : ""} au panier ✅`);
      navigate(`/restaurants/${o.restaurant_id}?cart=open`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ce restaurant n'est plus disponible.");
    } finally { setReordering(null); }
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/food/orders/mine")
      .then((r) => { if (!cancelled) setOrders(r.data?.orders || []); })
      .catch((e) => { if (!cancelled) setError(e?.response?.status === 401 ? "Connectez-vous pour voir vos commandes." : "Erreur de chargement."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF9F2] pb-24" data-testid="my-orders-page">
      <AppHeader variant="app" />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/passenger" className="inline-flex items-center gap-1 text-sm font-bold text-[#75807A] hover:text-[#F26419] mb-3" data-testid="my-orders-back">
          <ArrowLeft className="w-4 h-4" /> Retour
        </Link>
        <div className="flex items-center gap-2 mb-1">
          <Utensils className="w-6 h-6 text-[#059669]" />
          <h1 className="font-head font-black text-2xl tracking-tight">Mes commandes</h1>
        </div>
        <p className="text-sm text-[#75807A]">Historique complet de vos commandes Nold Food.</p>

        {loading && (
          <div className="mt-8 flex items-center justify-center gap-2 text-[#75807A]" data-testid="my-orders-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Chargement…
          </div>
        )}

        {!loading && error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="my-orders-error">
            {error}
          </div>
        )}

        {!loading && !error && orders.length === 0 && (
          <div className="mt-8 text-center rounded-2xl border-2 border-dashed border-[#E2DFD8] bg-white p-8" data-testid="my-orders-empty">
            <Utensils className="w-10 h-10 mx-auto text-[#75807A]" />
            <div className="mt-3 font-head font-black text-lg">Aucune commande pour l'instant</div>
            <p className="mt-1 text-sm text-[#75807A]">Découvrez nos restaurants et commandez votre premier repas.</p>
            <Link to="/restaurants" className="mt-4 inline-block h-11 px-5 rounded-full bg-[#059669] hover:bg-[#047857] text-white font-bold text-sm leading-[44px]" data-testid="my-orders-cta-restaurants">
              Voir les restaurants
            </Link>
          </div>
        )}

        {!loading && !error && orders.length > 0 && (
          <ul className="mt-5 space-y-3">
            {orders.map((o) => {
              const st = STATUS_STYLE[o.status] || STATUS_STYLE.awaiting_payment;
              const Icon = st.icon;
              return (
                <li key={o.id} data-testid={`my-order-${o.id}`} className="rounded-2xl bg-white border border-[#E2DFD8] hover:border-[#059669]/60 hover:shadow-md transition-all overflow-hidden">
                  <Link
                    to={`/food/order/${o.id}`}
                    className="flex items-center gap-3 p-4"
                  >
                    <div className={`w-11 h-11 shrink-0 rounded-full grid place-items-center ${st.bg}`}>
                      <Icon className={`w-5 h-5 ${st.color} ${["paid", "resto_accepted", "ready_for_pickup"].includes(o.status) ? "animate-spin" : ""}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline justify-between gap-2">
                        <div className="font-head font-bold text-[#1A1A1A] truncate">{o.restaurant_name}</div>
                        <div className="font-black text-[#1A1A1A] tabular-nums shrink-0">{(o.total_eur || 0).toFixed(2)} €</div>
                      </div>
                      <div className="text-xs text-[#75807A] truncate flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3 shrink-0" /> {o.delivery_address}
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${st.bg} ${st.color}`}>
                          {o.status_label}
                        </span>
                        <span className="text-[11px] text-[#75807A]">
                          {fmtDate(o.created_at)}
                        </span>
                        <span className="text-[11px] text-[#75807A]">
                          · {o.items_count} article{o.items_count > 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#75807A] shrink-0" />
                  </Link>
                  {/* Bouton Recommander — recrée un panier identique et ouvre la vitrine du resto.
                      Visible uniquement pour les commandes terminées ou payées (pas les brouillons). */}
                  {["delivered", "completed", "paid", "picked_up", "out_for_delivery"].includes(o.status) && o.restaurant_id && (o.items?.length > 0) && (
                    <div className="border-t border-[#F0EBE1] px-4 py-2.5 bg-[#FDF8F0]">
                      <button
                        onClick={() => reorder(o)}
                        disabled={reordering === o.id}
                        data-testid={`my-order-reorder-${o.id}`}
                        className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-xl bg-[#F26419] hover:bg-[#D94D0E] text-white text-sm font-bold disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {reordering === o.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />}
                        {reordering === o.id ? "Ajout au panier…" : "🔁 Recommander"}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
